import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { OnboardingFocus } from '@/lib/onboarding';

export interface OnboardingAnswers {
  focus: OnboardingFocus | null;
  roleId: string | null;
  companySize: string | null;
  useCases: string[];
  priorTool: string | null;
  workspaceName: string;
  invitedEmails: string[];
}

export interface ChecklistState {
  createdBoard: boolean;
  invitedTeammate: boolean;
  triedAi: boolean;
  connectedIntegration: boolean;
  installedMobile: boolean;
}

interface OnboardingState {
  completed: boolean;
  skipped: boolean;
  completedAt: string | null;
  step: number;               // current wizard step
  answers: OnboardingAnswers;
  seededBoardIds: string[];
  checklist: ChecklistState;
  checklistDismissed: boolean;

  setStep: (step: number) => void;
  patchAnswers: (patch: Partial<OnboardingAnswers>) => void;
  toggleUseCase: (uc: string) => void;
  addInvitedEmail: (email: string) => void;
  removeInvitedEmail: (email: string) => void;
  markCompleted: (seededBoardIds: string[]) => void;
  skip: () => void;
  restart: () => void;

  setChecklist: (patch: Partial<ChecklistState>) => void;
  dismissChecklist: () => void;
}

function defaultAnswers(): OnboardingAnswers {
  return {
    focus: null,
    roleId: null,
    companySize: null,
    useCases: [],
    priorTool: null,
    workspaceName: '',
    invitedEmails: [],
  };
}

function defaultChecklist(): ChecklistState {
  return {
    createdBoard: false,
    invitedTeammate: false,
    triedAi: false,
    connectedIntegration: false,
    installedMobile: false,
  };
}

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => ({
      completed: false,
      skipped: false,
      completedAt: null,
      step: 0,
      answers: defaultAnswers(),
      seededBoardIds: [],
      checklist: defaultChecklist(),
      checklistDismissed: false,

      setStep: (step) => set({ step }),
      patchAnswers: (patch) => set((s) => ({ answers: { ...s.answers, ...patch } })),
      toggleUseCase: (uc) => set((s) => {
        const has = s.answers.useCases.includes(uc);
        return {
          answers: {
            ...s.answers,
            useCases: has ? s.answers.useCases.filter((x) => x !== uc) : [...s.answers.useCases, uc],
          },
        };
      }),
      addInvitedEmail: (email) => {
        const e = email.trim().toLowerCase();
        if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return;
        if (get().answers.invitedEmails.includes(e)) return;
        set((s) => ({ answers: { ...s.answers, invitedEmails: [...s.answers.invitedEmails, e] } }));
      },
      removeInvitedEmail: (email) => set((s) => ({
        answers: { ...s.answers, invitedEmails: s.answers.invitedEmails.filter((x) => x !== email) },
      })),
      markCompleted: (seededBoardIds) => set({
        completed: true,
        skipped: false,
        completedAt: new Date().toISOString(),
        seededBoardIds,
        checklist: { ...defaultChecklist(), createdBoard: true },
      }),
      skip: () => set({
        completed: true,
        skipped: true,
        completedAt: new Date().toISOString(),
      }),
      restart: () => set({
        completed: false,
        skipped: false,
        completedAt: null,
        step: 0,
        answers: defaultAnswers(),
        seededBoardIds: [],
        checklist: defaultChecklist(),
        checklistDismissed: false,
      }),

      setChecklist: (patch) => set((s) => ({ checklist: { ...s.checklist, ...patch } })),
      dismissChecklist: () => set({ checklistDismissed: true }),
    }),
    { name: 'myday-onboarding', version: 1 },
  ),
);
